import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, Wrench, Shield, Clock, FileCheck, Banknote, ArrowRight, ArrowLeft, BedDouble, Bath, Maximize, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';
import Footer from '../components/sections/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const iconMap = { Home, Wrench, Shield, Clock, FileCheck, Banknote };

// ===== MAIN PAGE =====
const SingleFamilyHomes = () => {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [services, setServices] = useState([]);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [svcRes, locRes] = await Promise.all([
          axios.get(`${API}/admin/hb-services`),
          axios.get(`${API}/admin/catalog-locations`),
        ]);
        setServices(svcRes.data.services || []);
        setLocations(locRes.data.locations || []);
      } catch {}
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Catalog Section */}
      <section className="py-20 pt-28">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-800 mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            {isZh ? '方案目录' : 'The Catalog'}
          </h2>
          <p className="text-gray-600 mb-12 max-w-3xl leading-relaxed">
            {isZh ? 'ALIGCUS方案目录根据当地建筑风格量身定制，并已通过特定辖区的预审批。请选择您的位置以查看适用于您所在地区的方案。' : 'ALIGCUS Catalog designs are tailored to local architectural styles and pre-approved for use in specific jurisdictions. Please select your location to see designs for your area.'}
          </p>
          <div className="grid md:grid-cols-2 gap-10">
            {locations.map(loc => (
              <Link key={loc.id} to={`/single-family-homes/${loc.id}`} className="group">
                <h3 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Georgia, serif' }}>{loc.name}</h3>
                <div className="rounded-xl overflow-hidden shadow-lg">
                  {loc.image ? <img src={loc.image} alt={loc.name} className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-72 bg-gray-100" />}
                </div>
                <p className="text-gray-500 text-sm mt-3 italic">{isZh ? loc.description_zh : loc.description_en}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

// ===== CATALOG LIST PAGE (per location) =====
export const CatalogList = () => {
  const { locationId } = useParams();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [location, setLocation] = useState(null);
  const [homes, setHomes] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [locRes, homeRes] = await Promise.all([
          axios.get(`${API}/admin/catalog-locations`),
          axios.get(`${API}/admin/catalog-homes`),
        ]);
        setLocation((locRes.data.locations || []).find(l => l.id === locationId));
        setHomes((homeRes.data.homes || []).filter(h => h.location === locationId));
      } catch {}
    };
    load();
  }, [locationId]);

  if (!location) return <div className="min-h-screen flex items-center justify-center pt-20"><div className="w-10 h-10 border-4 border-red-600 border-t-transparent rounded-full animate-spin" /></div>;

  const sfHomes = homes.filter(h => h.category === 'single-family');
  const aduHomes = homes.filter(h => h.category === 'adu');

  return (
    <div className="min-h-screen bg-white pt-20">
      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Breadcrumb */}
        <div className="text-sm text-gray-500 mb-6"><Link to="/single-family-homes" className="hover:text-red-600">The Catalog</Link> / {location.name}</div>
        <h1 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Georgia, serif' }}>The Catalog: {location.name}</h1>
        <p className="text-gray-600 mb-10 italic max-w-3xl">{isZh ? location.description_zh : location.description_en}</p>

        {/* Single Family Homes */}
        {sfHomes.length > 0 && (
          <>
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Single-Family Homes</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {sfHomes.map(home => (
                <Link key={home.id} to={`/single-family-homes/${locationId}/${home.id}`} className="group">
                  <div className="rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                    {home.image ? <img src={home.image} alt={home.name} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-48 bg-gray-100" />}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mt-3">{home.name}</h3>
                  <p className="text-gray-500 text-sm">{home.sqft} sq ft, {home.bedrooms} bd / {home.bathrooms} ba</p>
                  {home.pre_approved && <p className="text-red-600 text-xs italic mt-1">Pre-approved{home.approved_by ? ` by ${home.approved_by}` : ''}</p>}
                </Link>
              ))}
            </div>
          </>
        )}

        {/* ADUs */}
        {aduHomes.length > 0 && (
          <>
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Accessory Dwelling Units (ADUs)</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {aduHomes.map(home => (
                <Link key={home.id} to={`/single-family-homes/${locationId}/${home.id}`} className="group">
                  <div className="rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                    {home.image ? <img src={home.image} alt={home.name} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-48 bg-gray-100" />}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mt-3">{home.name}</h3>
                  <p className="text-gray-500 text-sm">{home.sqft} sq ft, {home.bedrooms} bd / {home.bathrooms} ba</p>
                  {home.pre_approved && <p className="text-red-600 text-xs italic mt-1">Pre-approved{home.approved_by ? ` by ${home.approved_by}` : ''}</p>}
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

// ===== HOME DETAIL PAGE =====
export const CatalogHomeDetail = () => {
  const { locationId, homeId } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [home, setHome] = useState(null);
  const [location, setLocation] = useState(null);
  const [allHomes, setAllHomes] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [locRes, homeRes] = await Promise.all([
          axios.get(`${API}/admin/catalog-locations`),
          axios.get(`${API}/admin/catalog-homes`),
        ]);
        setLocation((locRes.data.locations || []).find(l => l.id === locationId));
        const filtered = (homeRes.data.homes || []).filter(h => h.location === locationId);
        setAllHomes(filtered);
        setHome(filtered.find(h => h.id === homeId));
      } catch {}
    };
    load();
  }, [locationId, homeId]);

  if (!home) return <div className="min-h-screen flex items-center justify-center pt-20"><div className="w-10 h-10 border-4 border-red-600 border-t-transparent rounded-full animate-spin" /></div>;

  const idx = allHomes.findIndex(h => h.id === homeId);
  const nextHome = idx >= 0 && idx < allHomes.length - 1 ? allHomes[idx + 1] : null;
  const features = (isZh ? home.features_zh : home.features_en) || '';

  return (
    <div className="min-h-screen bg-white pt-20">
      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Breadcrumb */}
        <div className="text-sm text-gray-500 mb-6">
          <Link to="/single-family-homes" className="hover:text-red-600">The Catalog</Link> / <Link to={`/single-family-homes/${locationId}`} className="hover:text-red-600">{location?.name}</Link> / {home.name}
        </div>

        <h1 className="text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Georgia, serif' }}>{home.name}</h1>

        {/* Main Image */}
        {home.image && (
          <div className="rounded-xl overflow-hidden shadow-lg mb-8">
            <img src={home.image} alt={home.name} className="w-full" />
          </div>
        )}

        <p className="text-gray-500 italic mb-6">concept design by ALIGCUS</p>

        {/* Features */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-3">Features of {home.name}</h2>
          <ul className="space-y-2">
            <li className="flex items-center gap-2 text-gray-700"><Maximize className="w-4 h-4 text-red-600" />{home.sqft} square feet</li>
            <li className="flex items-center gap-2 text-gray-700"><BedDouble className="w-4 h-4 text-red-600" />{home.bedrooms} bedrooms, {home.bathrooms} bathroom</li>
            {features.split('\n').filter(f => f.trim()).map((f, i) => (
              <li key={i} className="flex items-center gap-2 text-gray-700"><Check className="w-4 h-4 text-red-600" />{f.trim()}</li>
            ))}
          </ul>
        </div>

        {/* Image 2 — interleaved */}
        {home.image2 && (
          <div className="rounded-xl overflow-hidden shadow-lg mb-8">
            <img src={home.image2} alt={home.name} className="w-full" />
          </div>
        )}

        {/* Description */}
        <p className="text-gray-700 leading-relaxed mb-8">{isZh ? home.description_zh : home.description_en}</p>

        {/* Image 3 — interleaved */}
        {home.image3 && (
          <div className="rounded-xl overflow-hidden shadow-lg mb-8">
            <img src={home.image3} alt={home.name} className="w-full" />
          </div>
        )}

        {/* Floor Plan(s) */}
        {(Array.isArray(home.floor_plan_image) ? home.floor_plan_image : (home.floor_plan_image ? [home.floor_plan_image] : [])).filter(x=>x).map((img, i) => (
          <div key={i} className="rounded-xl overflow-hidden shadow-lg mb-8">
            <img src={img} alt={`${home.name} floor plan ${i+1}`} className="w-full" />
          </div>
        ))}

        {/* Inquiry */}
        <div className="mb-8">
          <Link to="/estimate"><Button className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-3">{isZh ? '咨询此方案' : 'Inquire about this design'}</Button></Link>
        </div>

        {/* Next */}
        <div className="flex justify-between items-center pt-8 border-t">
          <Button variant="outline" onClick={() => navigate(`/single-family-homes/${locationId}`)}><ArrowLeft className="w-4 h-4 mr-2" />{isZh ? '返回' : 'Back'}</Button>
          {nextHome && <Link to={`/single-family-homes/${locationId}/${nextHome.id}`}><Button className="bg-gray-900 text-white">Next: {nextHome.name} <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SingleFamilyHomes;
